/**
 * World <-> storey-map image mapping.
 *
 * The map camera sits above the storey looking straight down (`forward = -worldUp`) with
 * `up = worldForward`, so the image plane is spanned by the two non-vertical World axes - which
 * pair that is depends on the World's up axis: X/Z in a Y-up World, X/Y in a Z-up one. Deriving
 * the axes from the camera vectors keeps the map, the marker and picking consistent in both,
 * where hardcoding X/Z silently reads the vertical axis as a map coordinate.
 *
 * Verified against real snapshots: render a single entity through
 * StoreyViewsPlugin._arrangeStoreyMapCamera and locate its pixels.
 */

export interface AxisMapping {
  /** World axis index (0=X, 1=Y, 2=Z). */
  axis: number;
  /** 1 when the image axis grows with the World axis, -1 when it is mirrored. */
  sign: number;
}

export interface MapAxes {
  /** World axis shown along image +X (rightwards). */
  right: AxisMapping;
  /** World axis shown along image +Y (downwards). */
  down: AxisMapping;
  /** Vertical World axis, projected away by the map camera. */
  vertical: number;
}

export interface MapImageSize {
  width: number;
  height: number;
}

function dominantAxis(v: number[]): AxisMapping {
  let axis = 0;
  for (let i = 1; i < 3; i++) {
    if (Math.abs(v[i]) > Math.abs(v[axis])) axis = i;
  }
  return { axis, sign: v[axis] >= 0 ? 1 : -1 };
}

function cross(a: number[], b: number[]): number[] {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

export function mapAxes(worldUp: number[], worldForward: number[]): MapAxes {
  const forward = worldUp.map((n) => -n);
  return {
    right: dominantAxis(cross(forward, worldForward)),
    down: dominantAxis(worldForward.map((n) => -n)),
    vertical: dominantAxis(worldUp).axis,
  };
}

/** Position of a World point along an image axis, as a 0..1 fraction of the framed box. */
function normalized(aabb: number[], worldPos: number[], mapping: AxisMapping): number {
  const min = aabb[mapping.axis];
  const size = aabb[mapping.axis + 3] - min;
  const fraction = size === 0 ? 0.5 : (worldPos[mapping.axis] - min) / size;
  return mapping.sign > 0 ? fraction : 1 - fraction;
}

export function worldPosToImagePos(
  aabb: number[],
  worldPos: number[],
  size: MapImageSize,
  axes: MapAxes,
  imagePos: number[],
): boolean {
  imagePos[0] = Math.floor(normalized(aabb, worldPos, axes.right) * size.width);
  imagePos[1] = Math.floor(normalized(aabb, worldPos, axes.down) * size.height);

  return (
    imagePos[0] >= 0 && imagePos[0] < size.width && imagePos[1] >= 0 && imagePos[1] < size.height
  );
}

export function imagePosToWorldPos(
  aabb: number[],
  imagePos: number[],
  size: MapImageSize,
  axes: MapAxes,
): number[] {
  const worldPos = [0, 0, 0];

  const place = (mapping: AxisMapping, fraction: number) => {
    const min = aabb[mapping.axis];
    const extent = aabb[mapping.axis + 3] - min;
    worldPos[mapping.axis] = min + extent * (mapping.sign > 0 ? fraction : 1 - fraction);
  };

  place(axes.right, imagePos[0] / size.width);
  place(axes.down, imagePos[1] / size.height);
  worldPos[axes.vertical] = (aabb[axes.vertical] + aabb[axes.vertical + 3]) / 2;

  return worldPos;
}

/** Aspect ratio (height / width) of a map image framing the given box. */
export function mapAspect(aabb: number[], axes: MapAxes): number {
  const across = aabb[axes.right.axis + 3] - aabb[axes.right.axis];
  const down = aabb[axes.down.axis + 3] - aabb[axes.down.axis];
  return across === 0 ? 1 : Math.abs(down / across);
}
